import { useContext, useEffect, useState } from 'react'
//import { enqueueSnackbar } from 'notistack'
import moment from 'moment-timezone'
import parseHtml from 'html-react-parser'

//import { Typography, Switch, Stack } from '@mui/material'
import { TextField, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'

import AppleIcon from '@mui/icons-material/Apple'
import AndroidIcon from '@mui/icons-material/Android'
import EditIcon from '@mui/icons-material/Edit'

import { AppContext } from '/providers/app-context.js'

import { getTemplateData, renameTemplate } from '/providers/templates-api' // setGoogleBarcodeHandling
import ClickToCopy from '/components/click-to-copy'

import Stamp from './scanner-stamp'
import Loyalty from './scanner-loyalty'
import Gift from './scanner-gift'
import EventEditor from './event-editor'
import StyledPaper from '../paper.jsx'

const DetailSummary = ({ template }) => {
  const { apiSource, apiKey, refreshTemplate, setProcessing } = useContext(AppContext)
  const [templateName, setTemplateName] = useState(template.name)
  const [editFormVisible, setEditFormVisible] = useState(false)
  const [savingName, setSavingName] = useState(false)
  const isTemplateNameValid = templateName.trim().length >= 3

  useEffect(() => {
    if (template?.name) setTemplateName(template.name)
  }, [template?.name])

  if (!template) return null

  const saveTemplateName = async () => {
    setProcessing(true)
    try {
      await renameTemplate({ apiSource, apiKey, templateId: template.id, name: templateName })
      // TODO handle error
      await refreshTemplate(template.id)
    } finally {
      setProcessing(false)
    }
  }

  const openEditDialog = () => {
    setTemplateName(template.name)
    setEditFormVisible(true)
  }

  const closeEditDialog = () => {
    setTemplateName(template.name)
    setSavingName(false)
    setEditFormVisible(false)
  }

  const handleSaveTemplateName = async () => {
    if (!isTemplateNameValid || savingName) return
    setSavingName(true)
    try {
      await saveTemplateName()
      setEditFormVisible(false)
    } finally {
      setSavingName(false)
    }
  }

  return (
    <div className="max-w-[600px]">
      <StyledPaper>
        {dataItem("Template id", <ClickToCopy>{template.id}</ClickToCopy>)}
        <div className='flex justify-between w-full gap-10 mb-3'>
          <div className='text-nowrap'>Template Name</div>
          <div className='flex flex-row gap-2'>
            <div className='font-bold'>{template.name}</div>
            <EditIcon className="ml-2 cursor-pointer" onClick={openEditDialog} />
          </div>
        </div>
        <br />
        {dataItem("Status", template.archived ? <span className='red'>Archived</span> : <span className='green'>Active</span>)}
        <br />
        {dataItem("Pass type", template.pass_type)}
        {dataItem("Description", parseHtml(template.description))}
        <br />
        {dataItem("Created", moment(template.creation_timestamp, "YYYYMMDDHHmmss").format("DD MMM YYYY HH:mm"))}
        {dataItem("last modified", moment(template.last_modified_timestamp, "YYYYMMDDHHmmss").format("DD MMM YYYY HH:mm"))}
        <br />
        {dataItem("Test passes issued", template.test_passes_issued)}
        {dataItem("Live passes issued", template.live_passes_issued)}
        {dataItem("Active live passes", template.num_live_active_passes)}
        {dataItem("Expired live passes", template.num_live_expired_passes)}
        <br />
        {dataItem("Live passes not-synced", <><AppleIcon />{template.live_passes_not_synced_apple} <AndroidIcon className='ml-2' />{template.live_passes_not_synced_google}</>)}
        <br />
        {dataItem("Smartix pass type", template.pass_type_id)}
        {dataItem("Apple pass type identifier", template.apple_pass_type_identifier)}
        {dataItem("Apple pass type / sub-type", template.apple_pass_type + (template.apple_sub_type ? (" / " + template.apple_sub_type) : ""))}
        {dataItem("Google pass type / sub-type", template.google_pass_type + (template.google_sub_type ? (" / " + template.google_sub_type) : ""))}
        <br />
        {dataItem("Smartix scanner app compatible", template.smartix_scanner ? "Yes" : "No")}
      </StyledPaper>

      <ScannerDetailsWrapper template={template} />
      <EventEditorWrapper template={template} />
      <Dialog open={editFormVisible} onClose={closeEditDialog} fullWidth>
        <DialogTitle>Edit template name</DialogTitle>
        <DialogContent>
          <TextField
            label="Template Name"
            value={templateName}
            onChange={e => setTemplateName(e.target.value)}
            fullWidth
            autoFocus
            margin="dense"
            error={!isTemplateNameValid}
            helperText={!isTemplateNameValid ? 'Name must be at least 3 characters.' : ' '}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={closeEditDialog} disabled={savingName}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveTemplateName} disabled={!isTemplateNameValid || savingName}>{savingName ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

/*
      {dataItem("Disable Google barcodes on expiry *", <DisableBarcodeSwitch template={template} disableBarcode={disableBarcode} toggleGoogleBarcodeDisable={toggleGoogleBarcodeDisable} />)}
      <div>* May incur additional charges</div>
      <br />
*/

export default DetailSummary

/*
const DisableBarcodeSwitch = ({ template, disableBarcode, toggleGoogleBarcodeDisable }) => {

  if (template.archived) return disableBarcode === 1 ? "Yes" : "No"

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
      <Typography>No</Typography>
      <Switch checked={disableBarcode}
        onChange={toggleGoogleBarcodeDisable} />
      <Typography>Yes</Typography>
    </Stack>
  )
}
*/

const dataItem = (title, value) => <div className='flex justify-between w-full gap-10 mb-3'><div className='text-nowrap'>{title}</div><div className='font-bold'>{value}</div></div>

const ScannerDetailsWrapper = ({ template }) => {
  if (template.smartix_scanner === 1) {
    return <ScannerDetails template={template} />
  }
}

const ScannerDetails = ({ template }) => {

  const { apiSource, apiKey } = useContext(AppContext)
  const [templateData, setTemplateData] = useState()

  useEffect(() => {
    getInstanceData()
  }, [])

  const getInstanceData = async () => {
    const td = await getTemplateData({ apiSource, apiKey, templateId: template.id })
    setTemplateData(td.template)
  }

  if (templateData && template.pass_type_id === "smartix_stamp") {
    return (
      <div className='mt-10'>
        <Stamp template={templateData} setTemplate={setTemplateData} />
      </div>
    )
  }

  if (templateData && template.pass_type_id === "smartix_loyalty") {
    return (
      <div className='mt-10'>
        <Loyalty template={templateData} setTemplate={setTemplateData} />
      </div>
    )
  }

  if (templateData && template.pass_type_id === "smartix_gift") {
    return (
      <div className='mt-10'>
        <Gift template={templateData} setTemplate={setTemplateData} />
      </div>
    )
  }
}

const EventEditorWrapper = ({ template }) => {
  if (template.pass_type_id.replace(/_bg/, "") === "smartix_event") return <EventEditor template={template} />
}
